import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  Paper,
  Typography,
} from '@mui/material';
import { GangParticipant } from '../../types/gangCreationTypes';
import { useGangMemberSelection } from '../../hooks/useGangMemberSelection';
import { GangMemberSearchInput } from '../molecules/GangMemberSearchInput';
import { GangMemberSearchResults } from '../molecules/GangMemberSearchResults';
import { GangMemberSelectedList } from '../molecules/GangMemberSelectedList';

interface GangMemberSelectionProps {
  selectedIds: string[];
  participants: Record<string, GangParticipant>;
  onSelectUser: (user: {
    id: string;
    fullName: string;
    email: string;
    roles?: string[];
  }) => void;
  onRemoveUser: (userId: string) => void;
  excludeUserIds?: string[];
  maxSelections?: number;
  placeholder?: string;
  isLeaderSelection?: boolean;
  /** IDs of members that exist in the database (from initial gang data) */
  persistedMemberIds?: string[];
  /** When false, hide remove button for persisted members */
  isRemovalEnabled?: boolean;
}

export const GangMemberSelection = ({
  selectedIds,
  participants,
  onSelectUser,
  onRemoveUser,
  excludeUserIds,
  maxSelections,
  placeholder = 'Search users by name or email (minimum 3 characters)',
  isLeaderSelection = false,
  persistedMemberIds,
  isRemovalEnabled,
}: GangMemberSelectionProps) => {
  const {
    searchQuery,
    setSearchQuery,
    users,
    isLoading,
    searchError,
    shouldPreloadMore,
    isPreloadingMore,
    showPreloadIndicator,
    canLoadMore,
    handleLoadMore,
    canSelectMore,
    handleSelectUser,
    selectedParticipants,
  } = useGangMemberSelection({
    selectedIds,
    participants,
    onSelectUser,
    excludeUserIds,
    maxSelections,
  });

  if (searchError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to search users. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      <FormControl component="fieldset" fullWidth>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Available Users
            </Typography>

            <Box
              data-testid="auto-preload-flag"
              sx={{ display: showPreloadIndicator ? 'block' : 'none' }}
            />

            <GangMemberSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={placeholder}
              isLoading={isLoading}
            />

            {!canSelectMore && users.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Maximum number of users selected ({maxSelections}). Remove some
                users to select others.
              </Alert>
            )}

            <GangMemberSearchResults
              searchQuery={searchQuery}
              users={users}
              isLoading={isLoading}
              canSelectMore={canSelectMore}
              onSelectUser={handleSelectUser}
              onLoadMore={handleLoadMore}
              canLoadMore={canLoadMore}
            />

            {(shouldPreloadMore ||
              isPreloadingMore ||
              showPreloadIndicator) && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mt: 1,
                  gap: 1,
                }}
              >
                {isLoading && <CircularProgress size={16} />}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  data-testid="load-more-results-indicator"
                >
                  Loading more results...
                </Typography>
              </Box>
            )}

            <GangMemberSelectedList
              participants={selectedParticipants}
              onRemoveUser={onRemoveUser}
              isLeaderSelection={isLeaderSelection}
              maxSelections={maxSelections}
              persistedMemberIds={persistedMemberIds}
              isRemovalEnabled={isRemovalEnabled}
            />
          </Box>
        </Paper>
      </FormControl>
    </Box>
  );
};

export default GangMemberSelection;
